/**
 * Tenants list page. Group master-detail layout (spec 0103, replacing the
 * 0060 drill-in): a product-group nav-list on the left, the tenants table on
 * the right, with an "All" entry selected by default so every tenant shows
 * until a group is picked. The selection is URL-reflected via the
 * `?group=<name>` search param — absent means "All" — so browser back/forward
 * and deep-links work.
 *
 * SASADM-0107 (saas-auth SASATH-0212): the client-side data engine is gone.
 * The left pane is driven by the server's `productGroupCounts` facet (requested
 * on EVERY read — the facet deliberately ignores its own `productGroup`
 * selection, so the pane keeps every group at its full size while one is
 * selected) and the right pane by paged `q`/`status`/`productGroup` queries.
 *
 * SASADM-0111: the interactive column sort is back, but as a server concern.
 * 0107 had removed it because sorting the loaded page would have reordered 25
 * rows out of N; SASATH-0212's `sort=<field>,<dir>` orders the whole result set
 * before it is paged, so the header controls now just drive that param. Only
 * the server's allowlisted fields (`name`/`slug`/`status`/`createdAt`) get a
 * control — offering one for a column the server rejects would be a 400.
 *
 * SASADM-0113: that sort is URL-reflected too — `?sort=<field>,<dir>` beside
 * `?group=`, an absent param meaning the server's own default order — so a
 * sorted view is deep-linkable and browser back/forward walks through it.
 *
 * SASADM-0114: the rest of the query joins them. `q`, `status`, `page` and
 * `size` are read off the search params as well, so the WHOLE query is
 * URL-addressable — a shared "filtered, sorted, page 3" link now lands on that
 * exact view instead of silently dropping half of it, and a reload keeps it.
 * Every query write goes through one `setQuery` helper that REPLACES the
 * history entry: typing, paging and sorting are refinements of one view, not
 * places to walk back through, and pushing each of them buried the page the
 * user actually arrived from. A param sitting at its default is written as
 * ABSENT, which keeps the base URL clean and keeps the loader optimization
 * below alive.
 */
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink, useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { ProductDto, TenantPageResponse, TenantStatus } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { StatusChip } from '@/components/StatusChip';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { GroupMasterDetail } from '@/components/GroupMasterDetail';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';
import { getProducts } from '@/api/lookupCache';

/** Rows per page the loader asks for, and the initial pager selection. */
const DEFAULT_SIZE = 25;
/** The page sizes the pager offers, and the only ones `?size=` accepts. */
const PAGE_SIZES: readonly number[] = [10, 25, 50, 100];
/** The page the loader fetched, and where every filter change restarts. */
const DEFAULT_PAGE = 0;
/** Status the loader asks for, and the initial chip selection. */
const DEFAULT_STATUS: TenantStatus = 'ACTIVE';
/** The status chips, and the only values `?status=` accepts. */
const STATUS_FILTERS: readonly (TenantStatus | 'ALL')[] = [
  'ALL',
  'ACTIVE',
  'INACTIVE',
  'SUSPENDED',
];
/** Search-box idle time before the query goes to the server. */
const SEARCH_DEBOUNCE_MS = 300;
/** The columns SASATH-0212 allows in `sort` — no other column gets a control. */
type SortKey = 'name' | 'slug' | 'status' | 'createdAt';
/** The server's own default order, so the first page needs no `sort` at all. */
const DEFAULT_SORT_KEY: SortKey = 'slug';
const DEFAULT_SORT_DIR = 'asc';
/** The same allowlist, as a value — what `?sort=` is validated against. */
const SORT_KEYS: readonly SortKey[] = ['name', 'slug', 'status', 'createdAt'];

/**
 * SASADM-0113 — read the sort out of `?sort=<field>,<dir>`. Anything the server
 * would reject (an unknown field, a bad direction, a malformed pair) falls back
 * to the default order rather than being forwarded for a 400 INVALID_SORT: the
 * param is user-editable, so it is treated as a hint, not as input.
 */
function parseSortParam(value: string | null): { key: SortKey; dir: 'asc' | 'desc' } {
  const [field, dir] = (value ?? '').split(',');
  if (!SORT_KEYS.includes(field as SortKey) || (dir !== 'asc' && dir !== 'desc')) {
    return { key: DEFAULT_SORT_KEY, dir: DEFAULT_SORT_DIR };
  }
  return { key: field as SortKey, dir };
}

/**
 * SASADM-0114 — the same treatment for the other three params. Each one is
 * user-editable, so anything outside what the control itself can produce falls
 * back to that control's default rather than being forwarded to the server: a
 * mistyped link should open the plain list, not a 400.
 */
function parseStatusParam(value: string | null): TenantStatus | 'ALL' {
  return STATUS_FILTERS.find((s) => s === value) ?? DEFAULT_STATUS;
}

/** `?page=` as the zero-based index the pager uses; anything else is page 0. */
function parsePageParam(value: string | null): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : DEFAULT_PAGE;
}

/**
 * `?size=` restricted to the pager's own options — MUI's rows-per-page Select
 * warns on a value it does not offer, and the largest option (100) is already
 * inside the ceiling the server allows.
 */
function parseSizeParam(value: string | null): number {
  const n = Number(value);
  return PAGE_SIZES.includes(n) ? n : DEFAULT_SIZE;
}

export interface TenantsListLoaderData {
  firstPage: TenantPageResponse;
  products: ProductDto[];
}

export async function tenantsListLoader(): Promise<TenantsListLoaderData> {
  const [firstPage, products] = await Promise.all([
    apiAdmin.tenants.listPaged({
      page: 0,
      size: DEFAULT_SIZE,
      status: DEFAULT_STATUS,
      includeGroupCounts: true,
    }),
    getProducts().catch(() => [] as ProductDto[]),
  ]);
  return { firstPage, products };
}

export function TenantsList() {
  const { firstPage, products } = useLoaderData() as TenantsListLoaderData;
  const { t } = useCopy();
  const navigate = useNavigate();
  const snack = useSnackbar();
  // Spec 0103 — the selected group lives in the URL (`?group=<name>`) so
  // browser navigation/back and deep-links work. No param = "All".
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroup = searchParams.get('group');
  // SASADM-0113 — the sort is URL-reflected too (`?sort=<field>,<dir>`), so it
  // deep-links and survives back/forward. Read straight off the params like the
  // group rather than mirrored into state: a copy would go stale the moment the
  // URL changed under it (back/forward), leaving the header arrow, the request
  // and the address bar disagreeing. The default order carries no param.
  const { key: sortKey, dir: sortDir } = parseSortParam(searchParams.get('sort'));
  // SASADM-0114 — and the rest of the query with them, read the same way. The
  // param IS the state: no copy to go stale, and a pasted link or a back button
  // lands on exactly the view the URL describes rather than half of it.
  const q = searchParams.get('q') ?? '';
  const statusFilter = parseStatusParam(searchParams.get('status'));
  const page = parsePageParam(searchParams.get('page'));
  const perPage = parseSizeParam(searchParams.get('size'));

  /**
   * SASADM-0114 — the one way this page writes its query. Every write REPLACES
   * the current history entry: a filter, a sort and a page step are refinements
   * of one view, and pushing each of them buried the page the user came from
   * under a back stack they would have to click through. A param that equals
   * its default is DELETED rather than written, so the base URL stays clean and
   * `isLoaderParams` below still recognises it.
   */
  const setQuery = useCallback(
    (mut: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mut(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [fetched, setFetched] = useState<TenantPageResponse | null>(null);
  // The raw search box is the one part of the query that cannot be read
  // straight off the URL: round-tripping the router on every keystroke would
  // make it lag, and a history entry per character would be unusable. So it
  // keeps local state and debounce-writes `q` below — which leaves it to be
  // re-seeded whenever `q` moves from outside the box (back/forward, a shared
  // link). Adjusting state while rendering — React's own pattern for this, and
  // the repo's idiom for it (`AppDialog` seeds its form the same way) — rather
  // than an effect, which `react-hooks/set-state-in-effect` forbids and which
  // would paint one frame of stale text first.
  const [search, setSearch] = useState(q);
  const [prevQ, setPrevQ] = useState(q);
  if (q !== prevQ) {
    setPrevQ(q);
    // Only when `q` moved from OUTSIDE the box. The box's own debounced write
    // also lands here, and it is TRIMMED — re-seeding on that one would delete
    // whitespace out from under someone mid-phrase ("acme " -> "acme", caret to
    // the end, next word running into the last) every time they paused between
    // words. When `q` is already this box's trimmed text, what was typed stands.
    if (q !== search.trim()) setSearch(q);
  }

  // The loader already fetched exactly the unfiltered first page, so those
  // params are served from its data rather than re-queried; every other
  // combination is a round-trip. While one is in flight the previously loaded
  // page stays on screen instead of the table blanking out. The sort belongs in
  // this check too: the loader sends no `sort`, which the server reads as
  // slug-asc, so only that exact order is already in hand — any other one has
  // to be fetched.
  const isLoaderParams =
    page === DEFAULT_PAGE &&
    perPage === DEFAULT_SIZE &&
    q === '' &&
    statusFilter === DEFAULT_STATUS &&
    selectedGroup == null &&
    sortKey === DEFAULT_SORT_KEY &&
    sortDir === DEFAULT_SORT_DIR;
  const resp = isLoaderParams ? firstPage : (fetched ?? firstPage);

  const productMap = useMemo(() => {
    const m = new Map<number, ProductDto>();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  // Sync the search box into the URL's `q` after the user stops typing —
  // SASADM-0114 moved the target from component state to the address bar, so
  // only the settled term is ever reflected, never the keystrokes. A new term
  // is a new result set, so it restarts at the first page. Leading/trailing
  // whitespace is dropped, but nothing else is: `%`, `_` and `\` are matched
  // literally by the server, so escaping them would corrupt the search.
  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = search.trim();
      if (trimmed === q) return;
      setQuery((params) => {
        if (trimmed === '') params.delete('q');
        else params.set('q', trimmed);
        params.delete('page');
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search, q, setQuery]);

  // Fetch the page for the current params. The cleanup flag is the out-of-order
  // guard — a response whose params are no longer current is dropped rather
  // than rendered.
  useEffect(() => {
    if (isLoaderParams) return;
    let current = true;
    void (async () => {
      try {
        // Optional params are spread in only when set — under
        // `exactOptionalPropertyTypes` an explicit `undefined` is not the same
        // as an absent key, and `toQuery` would drop it anyway.
        const res = await apiAdmin.tenants.listPaged({
          page,
          size: perPage,
          ...(q ? { q } : {}),
          ...(statusFilter === 'ALL' ? {} : { status: statusFilter }),
          ...(selectedGroup != null ? { productGroup: selectedGroup } : {}),
          includeGroupCounts: true,
          sort: `${sortKey},${sortDir}`,
        });
        if (current) setFetched(res);
      } catch (err) {
        if (current && err instanceof ApiError) {
          snack.error(t(`errors.codes.${err.code}`, err.code));
        }
      }
    })();
    return () => {
      current = false;
    };
    // `snack`/`t` are stable for the life of the page and are not params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, q, statusFilter, selectedGroup, sortKey, sortDir, isLoaderParams]);

  // Every filter change restarts at the first page — the row that was at
  // offset N under the old filter means nothing under the new one — which now
  // means DELETING `page` rather than resetting a piece of state.
  function handleStatusChange(value: TenantStatus | 'ALL') {
    setQuery((params) => {
      if (value === DEFAULT_STATUS) params.delete('status');
      else params.set('status', value);
      params.delete('page');
    });
  }

  // Re-clicking the active column flips the direction; a new one starts at asc.
  // Either way the sort orders the whole result set on the server, so offset N
  // is a different row afterwards — back to the first page.
  function toggleSort(key: SortKey) {
    const dir = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    setQuery((params) => {
      // The default order is exactly what the server does with no `sort` at
      // all, so it is written as an ABSENT param — that keeps the URL clean and
      // keeps `isLoaderParams` true, so returning to it still answers page 0
      // from the loader's data without a request.
      if (key === DEFAULT_SORT_KEY && dir === DEFAULT_SORT_DIR) params.delete('sort');
      else params.set('sort', `${key},${dir}`);
      params.delete('page');
    });
  }

  function handleSizeChange(value: number) {
    setQuery((params) => {
      if (value === DEFAULT_SIZE) params.delete('size');
      else params.set('size', String(value));
      params.delete('page');
    });
  }

  function handlePageChange(value: number) {
    setQuery((params) => {
      if (value === DEFAULT_PAGE) params.delete('page');
      else params.set('page', String(value));
    });
  }

  // `null` selects the "All" row, which clears the param rather than setting it.
  // Written through the functional form so the rest of the query rides along
  // (SASADM-0113 for the sort, SASADM-0114 for `q`/`status`/`size`): those used
  // to live in component state, which a group change left untouched, so wiping
  // them here would be a behavior change rather than a URL move.
  function selectGroup(group: string | null) {
    setQuery((params) => {
      if (group == null) params.delete('group');
      else params.set('group', group);
      params.delete('page');
    });
  }

  // Reset clears the toolbar's own filters — the search term and the status —
  // and with them the paging those filters scoped. The group and the sort
  // belong to the pane and the header, not to this toolbar, so they stand.
  function resetFilters() {
    setSearch('');
    setQuery((params) => {
      params.delete('q');
      params.delete('status');
      params.delete('page');
      params.delete('size');
    });
  }

  // The facet spans the whole filtered set and ignores the group selection, so
  // it stays complete while a group is picked. Sorted by name for stable order.
  const groups = useMemo(
    () =>
      [...(resp.productGroupCounts ?? [])].sort((a, b) =>
        a.productGroup.localeCompare(b.productGroup),
      ),
    [resp.productGroupCounts],
  );
  // SASADM-0109 — the "All" badge has to agree with the table under it. The
  // facet counts only tenants that have a product, so its sum can read low
  // (a product-less tenant is in no group but is still a row). With "All"
  // selected the response's own total is the right number; a group-scoped
  // response carries no all-tenants total, so there the facet sum stands.
  const allCount =
    selectedGroup == null ? resp.totalElements : groups.reduce((sum, g) => sum + g.count, 0);

  // Whether an empty right pane reads "nothing here yet" vs "no match": only
  // the default search/status combination can mean "nothing here yet".
  const isFiltered = q !== '' || statusFilter !== DEFAULT_STATUS;

  return (
    <Box>
      <Breadcrumbs
        items={[{ label: t('nav.home', 'Home'), to: '/' }, { label: t('nav.tenants', 'Tenants') }]}
      />
      <PageHeader
        title={t('tenants.list.title', 'Tenants')}
        subtitle={t('tenants.list.subtitle', 'Customer organizations on the platform')}
        actions={
          <>
            <Button
              component={RouterLink}
              to="/workflows/new-tenant"
              variant="contained"
              startIcon={<AddIcon />}
            >
              {t('tenants.list.create', 'Create tenant')}
            </Button>
          </>
        }
      />

      <GroupMasterDetail
        groups={groups.map((g) => ({ key: g.productGroup, label: g.productGroup, count: g.count }))}
        selectedKey={selectedGroup}
        onSelect={selectGroup}
        allLabel={t('tenants.list.allGroups', 'All tenants')}
        allCount={allCount}
      >
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
            <TextField
              size="small"
              placeholder={t('common.search', 'Search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
              slotProps={{ htmlInput: { 'aria-label': t('common.search', 'Search') } }}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {STATUS_FILTERS.map((v) => (
                <Chip
                  key={v}
                  label={
                    v === 'ALL' ? t('tenants.list.filters.all', 'All') : t(`tenants.status.${v}`, v)
                  }
                  color={statusFilter === v ? 'primary' : 'default'}
                  onClick={() => handleStatusChange(v)}
                  size="small"
                />
              ))}
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetFilters}>{t('common.reset', 'Reset')}</Button>
          </Toolbar>
        </Paper>
        {resp.totalElements === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              {isFiltered
                ? t('tenants.list.noMatch', 'No tenants match your filters.')
                : selectedGroup != null
                  ? t('tenants.list.group.empty', 'No tenants in this group yet.')
                  : t('tenants.list.empty', 'No tenants yet. Create your first tenant to start.')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'name'}
                      direction={sortDir}
                      onClick={() => toggleSort('name')}
                    >
                      {t('tenants.list.columns.name', 'Name')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'slug'}
                      direction={sortDir}
                      onClick={() => toggleSort('slug')}
                    >
                      {t('tenants.list.columns.slug', 'Slug')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'status'}
                      direction={sortDir}
                      onClick={() => toggleSort('status')}
                    >
                      {t('tenants.list.columns.status', 'Status')}
                    </TableSortLabel>
                  </TableCell>
                  {/* Signup / Product / Brand are outside the server's sort
                      allowlist, so they stay plain — a control here would just
                      earn a 400 INVALID_SORT. */}
                  <TableCell>{t('tenants.list.columns.signupMode', 'Signup')}</TableCell>
                  <TableCell>{t('tenants.list.columns.product', 'Product')}</TableCell>
                  <TableCell>{t('tenants.list.columns.brand', 'Brand')}</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'createdAt'}
                      direction={sortDir}
                      onClick={() => toggleSort('createdAt')}
                    >
                      {t('tenants.list.columns.createdAt', 'Created')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resp.content.map((tn) => (
                  <TableRow
                    key={tn.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tenants/${tn.id}`)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {tn.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {tn.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip kind="tenantStatus" value={tn.status} />
                    </TableCell>
                    <TableCell>
                      <StatusChip kind="tenantSignupMode" value={tn.signupMode} />
                    </TableCell>
                    <TableCell>
                      {tn.productId
                        ? (productMap.get(tn.productId)?.displayName ?? `#${tn.productId}`)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {tn.brandId
                        ? `#${tn.brandId}`
                        : t('tenants.overview.inheritsBrand', 'Inherits product default')}
                    </TableCell>
                    <TableCell>{new Date(tn.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={resp.totalElements}
              page={page}
              onPageChange={(_e, p) => handlePageChange(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => handleSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={PAGE_SIZES}
            />
          </TableContainer>
        )}
      </GroupMasterDetail>
    </Box>
  );
}
